/**
 * Mapping-Editor (#1138 Tag 3) — Konto-Mapping + Aggregate-Formeln.
 */
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL || '';

function labelize(p) {
  return p.replace(/^guv_|^bilanz_|^cashflow_|^annahmen_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function InvestorMappingEditor() {
  const [tab, setTab] = useState('mapping');
  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-[#1A1A2E]">Mapping-Editor</h1>
      <div className="bg-[#F8F9FC] rounded-[10px] p-[3px] border border-[#E5E7EB] flex items-center gap-0.5 w-fit">
        {[
          { k: 'mapping', l: 'Konto-Mapping' },
          { k: 'aggregates', l: 'Aggregate-Formeln' },
          { k: 'ebitda', l: 'EBITDA-Regeln' },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-3 py-1 text-xs font-medium rounded-md ${
              tab === t.k ? 'bg-white text-[#1A1A2E] shadow-sm' : 'text-[#6B7280] hover:text-[#1A1A2E]'
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>
      {tab === 'mapping' && <MappingTab />}
      {tab === 'aggregates' && <AggregatesTab />}
      {tab === 'ebitda' && <EbitdaRulesTab />}
    </div>
  );
}

function MappingTab() {
  const [rows, setRows] = useState([]);
  const [ugbLines, setUgbLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [editCode, setEditCode] = useState('');
  const [editVorzeichen, setEditVorzeichen] = useState(1);
  const [editNotes, setEditNotes] = useState('');

  async function load() {
    setLoading(true);
    try {
      const [mRes, uRes] = await Promise.all([
        fetch(`${API}/api/investor/mapping`, { credentials: 'include' }),
        fetch(`${API}/api/investor/ugb-meta`, { credentials: 'include' }),
      ]);
      if (mRes.ok) setRows((await mRes.json()).rows || []);
      if (uRes.ok) setUgbLines((await uRes.json()).lines || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const ugbByCode = new Map(ugbLines.map(l => [l.code, l]));

  function startEdit(r) {
    setEditId(r.id);
    setEditCode(r.ugb_line_code || '');
    setEditVorzeichen(r.vorzeichen ?? 1);
    setEditNotes(r.notes || '');
  }

  async function save(id) {
    const meta = ugbByCode.get(editCode);
    const res = await fetch(`${API}/api/investor/mapping/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ugb_line_code: editCode,
        ugb_line_name: meta?.name || null,
        vorzeichen: editVorzeichen,
        notes: editNotes,
      }),
    });
    if (res.ok) { setEditId(null); await load(); }
    else { const d = await res.json(); alert('Fehler: ' + (d.error || res.status)); }
  }

  const filtered = rows.filter(r => {
    if (filter !== 'all' && r.bereich !== filter) return false;
    if (search && !r.position.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-3">
      <div className="text-xs text-[#6B7280] bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
        IST-Werte stammen aus Odoo-UGB-Bericht „Gewinn- und Verlustrechnung nach § 231 UGB (AT)". Mapping verknüpft Excel-Plan-Positionen mit UGB-Line-Codes. Konto-Tags pflegt das Buchhaltungsteam direkt in Odoo.
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filter} onChange={e => setFilter(e.target.value)} className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
          <option value="all">Alle Bereiche</option>
          <option value="guv">GuV</option>
          <option value="bilanz">Bilanz</option>
          <option value="cashflow">Cashflow</option>
        </select>
        <input
          type="text"
          placeholder="Position suchen…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white flex-1 max-w-xs"
        />
        <div className="text-xs text-[#9CA3AF]">{filtered.length} Positionen · {ugbLines.length} UGB-Lines</div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Excel-Position</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Bereich</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">UGB-Line</th>
              <th className="text-center px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Vz</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Notizen</th>
              <th className="text-right px-4 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Lädt…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Keine Daten</td></tr>
            ) : filtered.map(r => {
              const isEditing = editId === r.id;
              const hasCode = !!r.ugb_line_code;
              return (
                <tr key={r.id} className="border-t border-[#F1F2F4] align-top">
                  <td className="px-4 py-2 text-[#1A1A2E]">{labelize(r.position)}</td>
                  <td className="px-4 py-2 text-xs text-[#6B7280] uppercase">{r.bereich}</td>
                  <td className="px-4 py-2 text-xs">
                    {isEditing ? (
                      <select
                        value={editCode}
                        onChange={e => setEditCode(e.target.value)}
                        className="w-full text-xs px-2 py-1 border border-[#E5E7EB] rounded-md bg-white"
                      >
                        <option value="">— wählen —</option>
                        {ugbLines.map(l => (
                          <option key={l.code} value={l.code}>{l.code} — {l.name}</option>
                        ))}
                      </select>
                    ) : hasCode ? (
                      <div>
                        <span className="text-[#4B5563] font-mono">{r.ugb_line_code}</span>
                        {r.ugb_line_name && <span className="text-[#9CA3AF] ml-2">{r.ugb_line_name}</span>}
                      </div>
                    ) : (
                      <span className="text-[#9CA3AF] italic">— kein Mapping —</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <select value={editVorzeichen} onChange={e => setEditVorzeichen(parseInt(e.target.value, 10))} className="text-xs px-1 py-1 border border-[#E5E7EB] rounded-md">
                        <option value={1}>+</option>
                        <option value={-1}>−</option>
                      </select>
                    ) : (
                      <span className={`text-xs font-medium ${r.vorzeichen === -1 ? 'text-red-600' : 'text-[#4B5563]'}`}>{r.vorzeichen === -1 ? '−' : '+'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-[#4B5563]">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editNotes}
                        onChange={e => setEditNotes(e.target.value)}
                        className="w-full text-xs px-2 py-1 border border-[#E5E7EB] rounded-md"
                      />
                    ) : (r.notes || <span className="text-[#9CA3AF] italic">—</span>)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isEditing ? (
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => save(r.id)} className="text-xs px-2 py-1 rounded-md bg-[#338edc] text-white">OK</button>
                        <button onClick={() => setEditId(null)} className="text-xs px-2 py-1 rounded-md text-[#6B7280]">×</button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(r)} className="text-xs text-[#338edc] hover:underline">Bearbeiten</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AggregatesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editFormula, setEditFormula] = useState('');
  const [editNotes, setEditNotes] = useState('');

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/investor/aggregates`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRows(data.rows || []);
      }
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startEdit(r) {
    setEditId(r.id);
    setEditFormula(JSON.stringify(r.formula, null, 2));
    setEditNotes(r.notes || '');
  }

  async function save(id) {
    let formula;
    try { formula = JSON.parse(editFormula); }
    catch (e) { alert('Formel ist kein gültiges JSON: ' + e.message); return; }
    const res = await fetch(`${API}/api/investor/aggregates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ formula, notes: editNotes }),
    });
    if (res.ok) { setEditId(null); await load(); }
    else { const d = await res.json(); alert('Fehler: ' + (d.error || res.status)); }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-[#6B7280] bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
        Aggregate sind Summen aus Plan-Positionen. Bei Änderungen Cache wird automatisch invalidiert. Format: <code>[{`{"position":"x","vorzeichen":1}`}]</code>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Position</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Bereich</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Formel</th>
              <th className="text-right px-4 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Lädt…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Keine Aggregate</td></tr>
            ) : rows.map(r => {
              const isEditing = editId === r.id;
              return (
                <tr key={r.id} className="border-t border-[#F1F2F4] align-top">
                  <td className="px-4 py-2 text-[#1A1A2E] font-medium">{labelize(r.position)}</td>
                  <td className="px-4 py-2 text-xs text-[#6B7280] uppercase">{r.bereich}</td>
                  <td className="px-4 py-2 text-xs">
                    {isEditing ? (
                      <textarea
                        value={editFormula}
                        onChange={e => setEditFormula(e.target.value)}
                        rows={8}
                        className="w-full text-xs px-2 py-1 border border-[#E5E7EB] rounded-md font-mono"
                      />
                    ) : (
                      <details>
                        <summary className="cursor-pointer text-[#4B5563]">{(r.formula || []).length} Komponenten</summary>
                        <pre className="mt-1 text-[10px] text-[#6B7280] bg-[#F8F9FC] p-2 rounded-md max-h-48 overflow-auto">
                          {JSON.stringify(r.formula, null, 2)}
                        </pre>
                      </details>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isEditing ? (
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => save(r.id)} className="text-xs px-2 py-1 rounded-md bg-[#338edc] text-white">OK</button>
                        <button onClick={() => setEditId(null)} className="text-xs px-2 py-1 rounded-md text-[#6B7280]">×</button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(r)} className="text-xs text-[#338edc] hover:underline">Bearbeiten</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── EBITDA-Regeln (#1206) — 4 UI-pflegbare Listen für Pfad A ───
const EXCLUDE_REASONS = [
  { v: 'afa', l: 'AfA (§231 Add-Back)' },
  { v: 'finanz', l: 'Finanzergebnis' },
  { v: 'bilanz', l: 'Bilanz-Konto' },
  { v: 'sonstige', l: 'Sonstige' },
];
const GEMEIN_BUCKETS = [
  { v: 'verwaltung', l: 'Verwaltung' },
  { v: 'ma', l: 'MA-Verpflegung' },
  { v: 'support', l: 'Support' },
  { v: 'lageropt', l: 'Lageroptimierung' },
  { v: 'sonstige', l: 'Sonstige' },
];

function KontoSuche({ onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    let cancel = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/investor/ebitda-rules/konto-suche?q=${encodeURIComponent(q.trim())}`, { credentials: 'include' });
        if (res.ok && !cancel) setResults((await res.json()).rows || []);
      } finally { if (!cancel) setBusy(false); }
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [q]);
  return (
    <div className="relative">
      <input
        type="text"
        placeholder="Konto suchen (Code oder Name)…"
        value={q}
        onChange={e => setQ(e.target.value)}
        className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white w-full max-w-md"
      />
      {q.trim().length >= 2 && (
        <div className="absolute z-10 mt-1 w-full max-w-md bg-white border border-[#E5E7EB] rounded-md shadow-sm max-h-56 overflow-auto">
          {busy && <div className="px-3 py-2 text-xs text-[#9CA3AF]">Suche…</div>}
          {!busy && results.length === 0 && <div className="px-3 py-2 text-xs text-[#9CA3AF]">Keine Treffer</div>}
          {results.map(r => (
            <button
              key={r.code}
              onClick={() => { onPick(r); setQ(''); setResults([]); }}
              className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[#F8F9FC] border-t border-[#F1F2F4] first:border-t-0"
            >
              <span className="font-mono text-[#4B5563]">{r.code}</span>
              <span className="text-[#9CA3AF] ml-2">{r.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountRuleTable({ title, hint, endpoint, columns, picked, setPicked, extraDefault, renderExtra }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extra, setExtra] = useState(extraDefault);
  const [notes, setNotes] = useState('');

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/investor/ebitda-rules/${endpoint}`, { credentials: 'include' });
      if (res.ok) setRows((await res.json()).rows || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function add() {
    if (!picked) { alert('Bitte zuerst ein Konto wählen.'); return; }
    const res = await fetch(`${API}/api/investor/ebitda-rules/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ account_code: picked.code, account_name: picked.name, notes: notes || null, ...extra }),
    });
    if (res.ok) { setPicked(null); setNotes(''); setExtra(extraDefault); await load(); }
    else { const d = await res.json(); alert('Fehler: ' + (d.error || res.status)); }
  }

  async function remove(code) {
    const res = await fetch(`${API}/api/investor/ebitda-rules/${endpoint}/${encodeURIComponent(code)}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) await load();
    else { const d = await res.json(); alert('Fehler: ' + (d.error || res.status)); }
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-[#1A1A2E]">{title}</h3>
        <div className="text-[11px] text-[#9CA3AF] mt-0.5">{hint}</div>
      </div>
      <div className="px-4 py-3 border-b border-[#F1F2F4] space-y-2">
        {picked ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs bg-[#F8F9FC] border border-[#E5E7EB] rounded-md px-2 py-1">
              <span className="font-mono text-[#4B5563]">{picked.code}</span>
              <span className="text-[#9CA3AF] ml-2">{picked.name}</span>
            </span>
            <button onClick={() => setPicked(null)} className="text-xs text-[#6B7280] hover:underline">ändern</button>
          </div>
        ) : (
          <KontoSuche onPick={setPicked} />
        )}
        {picked && (
          <div className="flex items-center gap-2 flex-wrap">
            {renderExtra && renderExtra(extra, setExtra)}
            <input
              type="text" placeholder="Notiz (optional)" value={notes}
              onChange={e => setNotes(e.target.value)}
              className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white flex-1 min-w-[140px]"
            />
            <button onClick={add} className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium">Hinzufügen</button>
          </div>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
          <tr>
            {columns.map(c => (
              <th key={c.key} className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">{c.label}</th>
            ))}
            <th className="px-4 py-2 w-16"></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length + 1} className="px-4 py-6 text-center text-xs text-[#9CA3AF]">Lädt…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length + 1} className="px-4 py-6 text-center text-xs text-[#9CA3AF]">Keine Einträge</td></tr>
          ) : rows.map(r => (
            <tr key={r.account_code} className="border-t border-[#F1F2F4]">
              {columns.map(c => (
                <td key={c.key} className="px-4 py-2 text-xs text-[#4B5563]">{c.render ? c.render(r) : (r[c.key] ?? <span className="text-[#9CA3AF]">—</span>)}</td>
              ))}
              <td className="px-4 py-2 text-right">
                <button onClick={() => remove(r.account_code)} className="text-xs text-red-600 hover:underline">Löschen</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KostenstellenListEditor({ title, hint, endpoint }) {
  const [ids, setIds] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/investor/ebitda-rules/${endpoint}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        const list = d.kostenstelle_ids || [];
        setIds(list);
        setText(list.join(', '));
        setMeta(d.updated_at ? { at: d.updated_at, by: d.updated_by } : null);
      }
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    const parsed = text.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isInteger(n) && n > 0);
    const res = await fetch(`${API}/api/investor/ebitda-rules/${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ kostenstelle_ids: parsed }),
    });
    if (res.ok) { await load(); }
    else { const d = await res.json(); alert('Fehler: ' + (d.error || res.status)); }
  }

  const dirty = text.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isInteger(n) && n > 0).join(',') !== ids.join(',');

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-[#1A1A2E]">{title}</h3>
        <div className="text-[11px] text-[#9CA3AF] mt-0.5">{hint}</div>
      </div>
      <div className="px-4 py-3 space-y-2">
        {loading ? (
          <div className="text-xs text-[#9CA3AF]">Lädt…</div>
        ) : (
          <>
            <label className="block text-[11px] text-[#6B7280]">Kostenstellen-IDs (kommagetrennt)</label>
            <input
              type="text" value={text} onChange={e => setText(e.target.value)}
              placeholder="z.B. 6, 7, 45"
              className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white w-full max-w-md font-mono"
            />
            <div className="flex items-center gap-2">
              <button onClick={save} disabled={!dirty} className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium disabled:opacity-50">Speichern</button>
              {dirty && <span className="text-[11px] text-amber-600">Ungespeicherte Änderungen</span>}
              {meta && <span className="text-[11px] text-[#9CA3AF]">zuletzt {meta.at} · {meta.by}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EbitdaRulesTab() {
  const [pickEx, setPickEx] = useState(null);
  const [pickInc, setPickInc] = useState(null);
  return (
    <div className="space-y-4">
      <div className="text-xs text-[#6B7280] bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
        Diese vier Listen steuern Pfad A (Kostenrechnung). <strong>Ausschluss</strong> entfernt Konten aus der EBITDA-Rechnung
        (z.B. AfA via §231-Add-Back). <strong>Whitelist</strong> nimmt KS-lose Konten als Gemeinkosten auf.
        <strong> Sonstige Erträge/Aufwände</strong> ergänzen den Wasserfall um zusätzliche Kostenstellen.
        Änderungen wirken sofort auf EBITDA-Übersicht und EBITDA-Audit.
      </div>

      <AccountRuleTable
        title="Ausschluss-Konten"
        hint="Konten, die NICHT in die errechnete EBITDA einfließen (z.B. AfA, Finanzergebnis, Bilanz-Konten)."
        endpoint="exclude"
        picked={pickEx}
        setPicked={setPickEx}
        extraDefault={{ reason: 'sonstige' }}
        renderExtra={(extra, setExtra) => (
          <select value={extra.reason} onChange={e => setExtra({ reason: e.target.value })}
            className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
            {EXCLUDE_REASONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        )}
        columns={[
          { key: 'account_code', label: 'Konto', render: r => <span className="font-mono text-[#4B5563]">{r.account_code}</span> },
          { key: 'account_name', label: 'Bezeichnung' },
          { key: 'reason', label: 'Grund', render: r => (EXCLUDE_REASONS.find(o => o.v === r.reason)?.l || r.reason) },
          { key: 'notes', label: 'Notiz' },
        ]}
      />

      <AccountRuleTable
        title="Whitelist — KS-lose Konten"
        hint="Konten ohne Kostenstelle, die dennoch als Gemeinkosten in die EBITDA aufgenommen werden."
        endpoint="include"
        picked={pickInc}
        setPicked={setPickInc}
        extraDefault={{ gemein_bucket: 'sonstige' }}
        renderExtra={(extra, setExtra) => (
          <select value={extra.gemein_bucket} onChange={e => setExtra({ gemein_bucket: e.target.value })}
            className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
            {GEMEIN_BUCKETS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        )}
        columns={[
          { key: 'account_code', label: 'Konto', render: r => <span className="font-mono text-[#4B5563]">{r.account_code}</span> },
          { key: 'account_name', label: 'Bezeichnung' },
          { key: 'gemein_bucket', label: 'Bucket', render: r => (GEMEIN_BUCKETS.find(o => o.v === r.gemein_bucket)?.l || r.gemein_bucket) },
          { key: 'notes', label: 'Notiz' },
        ]}
      />

      <KostenstellenListEditor
        title="Sonstige Erträge"
        hint="Kostenstellen, deren Saldo als zusätzliche Ertrags-Station im Wasserfall erscheint (+)."
        endpoint="sonstige-ertraege"
      />

      <KostenstellenListEditor
        title="Sonstige Aufwände"
        hint="Kostenstellen, deren Saldo als zusätzliche Aufwands-Station im Wasserfall erscheint (−)."
        endpoint="sonstige-aufwand"
      />
    </div>
  );
}
